import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { User, hashPassword, buildN8nUrl, N8N_CONFIG, DELIVAI_INTERNAL_CNPJ } from "../config";
import {
  parseColaboradorCargoJson,
  buildColaboradorCargoJson,
  ensureCargoJsonForSidebar,
  type ColaboradorCargoJson,
} from "../types/collaboratorCargo";
import { parseColaboradorDiaria } from "../utils/collaboratorsWebhookMapper";
import {
  getClientDataForLogin,
  getCollaboratorDataForLogin,
  getCollaboratorProfileWithSession,
  getLicenseModulesData,
  getLicenseData,
  extractNomeEmpresaFromClientResponse,
} from "../utils/api";
import {
  clearLicenseDiasCache,
  readLicenseDiasCache,
  writeLicenseDiasCache,
} from "../utils/licenseStatus";
import { isDelivaiDevSession } from "../utils/delivaiInternal";
import {
  clearLoginAudience,
  parseAgendaEnabledFlag,
  persistLoginAudience,
} from "../utils/loginAudience";
import { ensureDelivaiInternalDevCargo } from "../utils/delivaiDevCargo";
import { KITCHEN_SWITCH_EVENT } from "../services/darkKitchen";

export const AUTH_CNPJ_ERR = {
  VALIDATE: "AUTH_CNPJ_VALIDATE",
  NOT_FOUND: "AUTH_CNPJ_NOT_FOUND",
  UNEXPECTED: "AUTH_CNPJ_UNEXPECTED",
  FIRST_ACCESS: "AUTH_FIRST_ACCESS",
} as const;

function collaboratorPhoneFromEmpresa(empresaData: Record<string, unknown>): string | undefined {
  const raw =
    empresaData.telefone ??
    empresaData.phone ??
    empresaData.telefone_colaborador ??
    empresaData.celular;
  const d = String(raw ?? "").replace(/\D/g, "");
  return d || undefined;
}

interface AuthContextType {
  user: User | null;
  login: (
    cnpj: string,
    password: string,
    options?: {
      cpf?: string;
      cnpjLoja?: string;
      internalDelivaiCnpj?: boolean;
      employeeId?: string;
    }
  ) => Promise<boolean>;
  logout: () => void;
  /** Recarrega cargo/perfil do colaborador no servidor (permissoes atualizadas sem relogin). */
  refreshCollaboratorSession: () => Promise<boolean>;
  loading: boolean;
  validateCnpj: (cnpj: string) => Promise<{ ok: boolean; senhaDefinida?: boolean }>;
  validateCollaboratorCpf: (
    cpf: string,
    options?: {
      cnpjLoja?: string;
      internalDelivaiCnpj?: boolean;
      storeHint?: {
        company_name?: string;
        nome_empresa?: string;
        nome?: string;
        cargo?: unknown;
        role?: unknown;
        employee_id?: string;
        senha_definida?: boolean;
        password_set?: boolean;
        agenda_enabled?: boolean;
      };
    }
  ) => Promise<{
    exists: boolean;
    firstAccess: boolean;
    cnpj?: string;
    cpf?: string;
  }>;
  clearCnpjValidation: () => void;
  cnpjValidated: boolean;
  clientData: any;
  error: string;
  licenseData: {
    mod_atendimento: boolean;
    mod_motoboy: boolean;
    mod_gerente: boolean;
    mod_agendamento: boolean;
    mod_financeiro: boolean;
  } | null;
  licenseSummary: {
    diasRestantes: number | null;
    loading: boolean;
    contaAtiva?: boolean | null;
  };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

function cargoJsonFromEmpresaResponse(empresaData: Record<string, unknown>): ColaboradorCargoJson {
  for (const key of ["cargo", "role"] as const) {
    const raw = empresaData?.[key];
    if (raw == null) continue;
    if (typeof raw === "string") {
      try {
        const parsed = parseColaboradorCargoJson(JSON.parse(raw));
        if (parsed) return ensureCargoJsonForSidebar(parsed);
      } catch {
        if (key === "cargo" && raw.trim()) {
          return ensureCargoJsonForSidebar(buildColaboradorCargoJson(raw.trim()));
        }
      }
      continue;
    }
    if (typeof raw === "object") {
      const parsed = parseColaboradorCargoJson(raw);
      if (parsed) return ensureCargoJsonForSidebar(parsed);
    }
  }
  const titulo =
    (typeof empresaData?.cargo_titulo === "string" ? empresaData.cargo_titulo : "") ||
    "Colaborador";
  return ensureCargoJsonForSidebar(buildColaboradorCargoJson(String(titulo)));
}

function cargoJsonForCollaboratorLogin(
  empresaData: Record<string, unknown>,
  _resolvedCnpj: string,
  internalDelivaiCnpj?: boolean
): ColaboradorCargoJson {
  let cargoJson = cargoJsonFromEmpresaResponse(empresaData);
  if (internalDelivaiCnpj) {
    cargoJson = ensureDelivaiInternalDevCargo(cargoJson);
  }
  return cargoJson;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [cnpjValidated, setCnpjValidated] = useState(false);
  const [clientData, setClientData] = useState<any>(null);
  const [error, setError] = useState('');
  const [licenseData, setLicenseData] = useState<{
    mod_atendimento: boolean;
    mod_motoboy: boolean;
    mod_gerente: boolean;
    mod_agendamento: boolean;
    mod_financeiro: boolean;
  } | null>(null);
  const [licenseSummary, setLicenseSummary] = useState<{
    diasRestantes: number | null;
    loading: boolean;
    contaAtiva: boolean | null;
  }>({ diasRestantes: null, loading: false, contaAtiva: null });
  const [initialized, setInitialized] = useState(false);
  const userRef = useRef<User | null>(null);
  userRef.current = user;
  const cnpjValidateGen = useRef(0);

  // Função para converter valor para boolean de forma mais robusta
  const convertToBoolean = (value: any): boolean => {
    console.log(`🔐 convertToBoolean - valor recebido:`, value, `tipo:`, typeof value);
    
    if (typeof value === 'boolean') {
      console.log(`🔐 convertToBoolean - é boolean, retornando:`, value);
      return value;
    }
    if (typeof value === 'string') {
      const result = value.toLowerCase() === 'true' || value === '1';
      console.log(`🔐 convertToBoolean - é string "${value}", retornando:`, result);
      return result;
    }
    if (typeof value === 'number') {
      const result = value === 1;
      console.log(`🔐 convertToBoolean - é number ${value}, retornando:`, result);
      return result;
    }
    console.log(`🔐 convertToBoolean - valor não reconhecido, retornando false`);
    return false;
  };

  useEffect(() => {
    // Evitar múltiplas inicializações
    if (initialized) {
      console.log("🔐 AuthContext já foi inicializado, pulando...");
      return;
    }

    console.log("🔐 useEffect DO AUTHCONTEXT EXECUTADO!");
    const initializeAuth = async () => {
      console.log("🔐 INICIANDO AUTH - useEffect executado");
      
      // Verificar se há usuário salvo no localStorage
      const savedUser = localStorage.getItem("delivai_user");
      console.log("🔐 savedUser encontrado:", savedUser ? "SIM" : "NÃO");
      
      if (savedUser) {
        try {
          const userData = JSON.parse(savedUser);
          console.log("🔐 userData parseado:", userData);
          setUser(userData);
          
          // Cache só como paint otimista — sempre revalidar page-verify
          // (senão módulos ligados no Dev não aparecem na sidebar da loja).
          const cachedModules = localStorage.getItem("license_modules");
          const cachedDias = readLicenseDiasCache(userData.cnpj);
          if (cachedDias) {
            setLicenseSummary({
              diasRestantes: cachedDias.diasRestantes,
              loading: true,
              contaAtiva: null,
            });
          } else {
            setLicenseSummary({ diasRestantes: null, loading: true, contaAtiva: null });
          }

          if (cachedModules) {
            try {
              const parsedModules = JSON.parse(cachedModules);
              setLicenseData(parsedModules);
            } catch {
              /* ignore stale cache */
            }
          }
          await fetchAndSetLicenseData(userData.cnpj);
        } catch (error) {
          console.error("🔐 Erro geral ao carregar usuário:", error);
          try {
            if (localStorage.getItem("delivai_delidesk_embed") === "1") {
              setLoading(false);
              setInitialized(true);
              return;
            }
          } catch {
            /* ignore */
          }
          localStorage.removeItem("delivai_user");
          localStorage.removeItem("license_modules");
          clearLicenseDiasCache();
        }
      }
      
      setLoading(false);
      setInitialized(true);
    };
    
    initializeAuth();
  }, [initialized]); // Adicionar initialized como dependência

  useEffect(() => {
    const onSwitch = (event: Event) => {
      const detail = (event as CustomEvent<{ cnpj?: string; nome?: string }>).detail;
      const digits = String(detail?.cnpj || "").replace(/\D/g, "");
      if (digits.length < 11) return;
      setUser((prev) => {
        if (!prev || prev.isCollaborator) return prev;
        const next = {
          ...prev,
          cnpj: digits,
          name: detail?.nome?.trim() || prev.name,
        };
        localStorage.setItem("delivai_user", JSON.stringify(next));
        localStorage.setItem("cnpj", digits);
        return next;
      });
    };
    window.addEventListener(KITCHEN_SWITCH_EVENT, onSwitch);
    return () => window.removeEventListener(KITCHEN_SWITCH_EVENT, onSwitch);
  }, []);

  const fetchLicenseDias = async (cnpj: string) => {
    setLicenseSummary((s) => ({ ...s, loading: true }));
    try {
      const result = await getLicenseData(cnpj);
      if (
        result?.success &&
        result.licenseData &&
        typeof result.licenseData.dias_restantes === "number"
      ) {
        const d = result.licenseData.dias_restantes;
        const contaAtiva = result.licenseData.conta_ativa !== false;
        setLicenseSummary({ diasRestantes: d, loading: false, contaAtiva });
        writeLicenseDiasCache(cnpj, d);
        return;
      }
    } catch (e) {
      console.error("🔐 Erro ao buscar dias da licença:", e);
    }
    setLicenseSummary((s) => ({
      diasRestantes: s.diasRestantes,
      loading: false,
      contaAtiva: s.contaAtiva,
    }));
  };

  // Módulos (page-verify) + dias (get-customer) em paralelo
  const fetchAndSetLicenseData = async (cnpj: string) => {
    setLicenseSummary((s) => ({ ...s, loading: true }));
    try {
      console.log("🔐 Buscando módulos e dias da licença para CNPJ:", cnpj);
      const settled = await Promise.allSettled([
        getLicenseModulesData(cnpj),
        getLicenseData(cnpj),
      ]);

      const modulesRes = settled[0];
      const licenseRes = settled[1];

      if (modulesRes.status === "fulfilled" && modulesRes.value) {
        const empresaData = modulesRes.value;
        const licenseModulesData = {
          mod_atendimento: convertToBoolean(empresaData.mod_atendimento),
          mod_motoboy: convertToBoolean(empresaData.mod_motoboy),
          mod_gerente: convertToBoolean(empresaData.mod_gerente),
          mod_agendamento: convertToBoolean(empresaData.mod_agendamento),
          mod_financeiro: convertToBoolean(
            empresaData.mod_financeiro ?? empresaData.module_finance_enabled
          ),
        };
        setLicenseData(licenseModulesData);
        localStorage.setItem("license_modules", JSON.stringify(licenseModulesData));
      } else {
        console.error("🔐 Falha ao obter módulos:", modulesRes);
      }

      if (licenseRes.status === "fulfilled" && licenseRes.value) {
        const result = licenseRes.value;
        if (
          result?.success &&
          result.licenseData &&
          typeof result.licenseData.dias_restantes === "number"
        ) {
          const d = result.licenseData.dias_restantes;
          const contaAtiva = result.licenseData.conta_ativa !== false;
          setLicenseSummary({ diasRestantes: d, loading: false, contaAtiva });
          writeLicenseDiasCache(cnpj, d);
        } else {
          setLicenseSummary((s) => ({
            diasRestantes: s.diasRestantes,
            loading: false,
            contaAtiva: s.contaAtiva,
          }));
        }
      } else {
        console.error("🔐 Falha ao obter dias da licença:", licenseRes);
        setLicenseSummary((s) => ({
          diasRestantes: s.diasRestantes,
          loading: false,
          contaAtiva: s.contaAtiva,
        }));
      }
    } catch (webhookError) {
      console.error("🔐 Erro ao buscar dados de licença:", webhookError);
      setLicenseSummary((s) => ({
        diasRestantes: s.diasRestantes,
        loading: false,
        contaAtiva: s.contaAtiva,
      }));
    }
  };

  const login = async (
    cnpj: string,
    password: string,
    options?: {
      cpf?: string;
      cnpjLoja?: string;
      internalDelivaiCnpj?: boolean;
      employeeId?: string;
    }
  ): Promise<boolean> => {
    try {
      const cleanCpf = options?.cpf?.replace(/\D/g, "") ?? "";
      console.log("🔐 Tentando fazer login com CNPJ:", cnpj, cleanCpf ? "CPF colaborador" : "");

       // Buscar dados do cliente via webhook n8n
       console.log("🔐 LOGIN - CHAMANDO check auth...");
       const clientData = cleanCpf
         ? await getCollaboratorDataForLogin(cleanCpf, {
             ...(options?.cnpjLoja ? { cnpjLoja: options.cnpjLoja } : {}),
             ...(options?.internalDelivaiCnpj ? { internalDelivaiCnpj: true } : {}),
           })
         : await getClientDataForLogin(cnpj);
       console.log("🔐 LOGIN - check auth RETORNOU:", clientData);
      
             console.log("🔐 Dados do cliente recebidos:", clientData);
       console.log("🔐 Estrutura completa do clientData:", JSON.stringify(clientData, null, 2));
       console.log("🔐 clientData.empresa:", clientData.empresa);
       console.log("🔐 typeof clientData.empresa:", typeof clientData.empresa);
       console.log("🔐 Array.isArray(clientData.empresa):", Array.isArray(clientData.empresa));

      if (!clientData.exists || !clientData.empresa) {
        throw new Error("Cliente não encontrado");
      }

             // Extrair dados dos módulos da licença e nicho da empresa
       // O webhook retorna os dados em clientData.empresa (que é um array com um objeto)
       // ou diretamente em clientData se for um objeto único
       const empresaData = clientData.empresa && Array.isArray(clientData.empresa) 
         ? clientData.empresa[0] 
         : clientData.empresa || clientData;
       
       console.log("🔐 empresaData extraído:", empresaData);
       console.log("🔐 empresaData.mod_atendimento (raw):", empresaData.mod_atendimento);
       console.log("🔐 typeof empresaData.mod_atendimento:", typeof empresaData.mod_atendimento);

      const resolvedCnpj =
        (empresaData.cnpj && String(empresaData.cnpj).replace(/\D/g, "")) ||
        cnpj.replace(/\D/g, "") ||
        DELIVAI_INTERNAL_CNPJ;

            // Login bem-sucedido (a verificação de senha já foi feita pelo webhook verify-password)
      const isCollaborator = cleanCpf.length > 0;
      const userData: User = {
        id: String(empresaData.id ?? empresaData.id_colaborador ?? (cleanCpf || cnpj)),
        name: (empresaData.nome || empresaData.name || "Empresa") as string,
        cnpj: resolvedCnpj,
        email: (empresaData.email_cliente || empresaData.email || "") as string,
        token: "local_token_" + (cleanCpf || resolvedCnpj),
        ...(isCollaborator
          ? {
              cpf: cleanCpf,
              isCollaborator: true,
              employeeId: options?.employeeId || (empresaData.employee_id as string | undefined) || (empresaData.id as string | undefined),
              phone: collaboratorPhoneFromEmpresa(empresaData as Record<string, unknown>),
              agendaEnabled: parseAgendaEnabledFlag(
                (empresaData as Record<string, unknown>).agenda_enabled
              ),
              cargoJson: cargoJsonForCollaboratorLogin(
                empresaData as Record<string, unknown>,
                resolvedCnpj,
                options?.internalDelivaiCnpj
              ),
              diaria: parseColaboradorDiaria(empresaData as Record<string, unknown>),
            }
          : {
              isCollaborator: false,
              cargoJson: undefined,
            }),
        };
       
               // Usar dados do login original (check-cnpj)
        const modulesData = {
          mod_atendimento: convertToBoolean(empresaData.mod_atendimento),
          mod_motoboy: convertToBoolean(empresaData.mod_motoboy),
          mod_gerente: convertToBoolean(empresaData.mod_gerente),
          mod_agendamento: convertToBoolean(empresaData.mod_agendamento),
          mod_financeiro: convertToBoolean(
            empresaData.mod_financeiro ?? empresaData.module_finance_enabled
          ),
        };
        
        console.log("🔐 RESULTADO FINAL DOS MÓDULOS (do login):");
        console.log("  - mod_atendimento:", modulesData.mod_atendimento, "(tipo:", typeof modulesData.mod_atendimento, ")");
        console.log("  - mod_motoboy:", modulesData.mod_motoboy, "(tipo:", typeof modulesData.mod_motoboy, ")");
        console.log("  - mod_gerente:", modulesData.mod_gerente, "(tipo:", typeof modulesData.mod_gerente, ")");
        console.log("  - mod_agendamento:", modulesData.mod_agendamento, "(tipo:", typeof modulesData.mod_agendamento, ")");

        setUser(userData);
        setLicenseData(modulesData);
        persistLoginAudience(
          options?.internalDelivaiCnpj
            ? "delivai_dev"
            : cleanCpf
              ? "colaboradores"
              : "empresa"
        );
        localStorage.setItem("delivai_user", JSON.stringify(userData));
        localStorage.setItem("cnpj", resolvedCnpj);
        if (cleanCpf) {
          localStorage.setItem("delivai_collaborator_cpf", cleanCpf);
        } else {
          localStorage.removeItem("delivai_collaborator_cpf");
        }
        localStorage.setItem("license_modules", JSON.stringify(modulesData)); // Salvar módulos
        
        // Log direto do que foi salvo no localStorage
        console.log("🔐 DADOS SALVOS NO LOCALSTORAGE:");
        console.log("  - license_modules:", JSON.stringify(modulesData));
        console.log("  - mod_atendimento salvo como:", modulesData.mod_atendimento);
        console.log("  - typeof mod_atendimento salvo:", typeof modulesData.mod_atendimento);

        console.log("✅ Login realizado com sucesso:", userData);

        try {
          await fetchAndSetLicenseData(resolvedCnpj);
        } catch (webhookError) {
          console.error("🔐 LOGIN - Erro ao atualizar módulos/dias da licença:", webhookError);
        }

        return true;
    } catch (error) {
      console.error("❌ Erro no login:", error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const validateCnpj = async (cnpj: string): Promise<{ ok: boolean; senhaDefinida?: boolean }> => {
    const gen = ++cnpjValidateGen.current;
    try {
      setError('');
      
      const data = await getClientDataForLogin(cnpj);
      if (gen !== cnpjValidateGen.current) return { ok: false };
      
      console.log("🔐 Resposta do webhook:", data);
      
             // Verificar se o cliente existe
       if (data.exists && data.empresa) {
         setCnpjValidated(true);
         
         // Extrair dados da empresa da mesma forma que no login
         const empresaData = data.empresa && Array.isArray(data.empresa) 
           ? data.empresa[0] 
           : data.empresa || data;

         const cleanCnpj = String(cnpj || "").replace(/\D/g, "");
         const displayNome =
           extractNomeEmpresaFromClientResponse({ ...data, ...empresaData }) ||
           (typeof empresaData.nome === "string" ? empresaData.nome.trim() : "") ||
           (typeof empresaData.nome_empresa === "string" ? empresaData.nome_empresa.trim() : "");
         
         // Adicionar o CNPJ ao clientData para uso posterior
         setClientData({
           ...empresaData,
           cnpj: cleanCnpj,
           nome: displayNome || empresaData.nome,
           nome_empresa: empresaData.nome_empresa || displayNome,
         });

         // Se a senha não foi criada, mostrar mensagem de primeiro acesso
         if (!empresaData.senha_definida) {
           setError(AUTH_CNPJ_ERR.FIRST_ACCESS);
           return { ok: true, senhaDefinida: false };
         }
         
         console.log("✅ Cliente encontrado com senha definida, prosseguindo para login");
         return { ok: true, senhaDefinida: true };
        
      } else if (data.exists === false) {
        setError(AUTH_CNPJ_ERR.NOT_FOUND);
        setCnpjValidated(false);
        setClientData(null);
        return { ok: false };
      } else {
        setError(AUTH_CNPJ_ERR.UNEXPECTED);
        setCnpjValidated(false);
        setClientData(null);
        return { ok: false };
      }
      
    } catch (error) {
      if (gen !== cnpjValidateGen.current) return { ok: false };
      console.error('Erro na validação do CNPJ:', error);
      setError(AUTH_CNPJ_ERR.VALIDATE);
      setCnpjValidated(false);
      setClientData(null);
      return { ok: false };
    } finally {
      if (gen === cnpjValidateGen.current) setLoading(false);
    }
  };

  const validateCollaboratorCpf = async (
    cpf: string,
    options?: {
      cnpjLoja?: string;
      internalDelivaiCnpj?: boolean;
      storeHint?: {
        company_name?: string;
        nome_empresa?: string;
        nome?: string;
        cargo?: unknown;
        role?: unknown;
        employee_id?: string;
        senha_definida?: boolean;
        password_set?: boolean;
        agenda_enabled?: boolean;
      };
    }
  ) => {
    try {
      setError("");

      const cleanCpf = cpf.replace(/\D/g, "");
      const hint = options?.storeHint;
      const senhaHintKnown =
        hint?.senha_definida === true ||
        hint?.senha_definida === false ||
        hint?.password_set === true ||
        hint?.password_set === false;
      const canUseResolveHint =
        Boolean(hint?.employee_id) &&
        Boolean(options?.cnpjLoja) &&
        senhaHintKnown;

      const data = canUseResolveHint
        ? (() => {
            const senhaDef =
              hint!.senha_definida === true || hint!.password_set === true;
            console.log(
              "🔐 Validação colaborador via resolve-login (sem 2ª chamada)"
            );
            return {
              exists: true,
              empresa: {
                nome: hint!.nome || "Colaborador",
                nome_completo: hint!.nome || "Colaborador",
                employee_id: hint!.employee_id,
                id: hint!.employee_id,
                senha_definida: senhaDef,
                password_set: senhaDef,
                cargo: hint!.cargo,
                role: hint!.role,
                cnpj: options!.cnpjLoja!.replace(/\D/g, ""),
                company_name: hint!.company_name || hint!.nome_empresa,
                nome_empresa: hint!.nome_empresa || hint!.company_name,
                cpf: cleanCpf,
                agenda_enabled: hint!.agenda_enabled === true,
              },
            };
          })()
        : await getCollaboratorDataForLogin(cleanCpf, {
            ...(options?.cnpjLoja ? { cnpjLoja: options.cnpjLoja } : {}),
            ...(options?.internalDelivaiCnpj ? { internalDelivaiCnpj: true } : {}),
          });

      console.log("🔐 Resposta validação colaborador:", data);

      if (data.exists && data.empresa) {
        setCnpjValidated(true);

        const empresaData =
          data.empresa && Array.isArray(data.empresa)
            ? data.empresa[0]
            : data.empresa || data;

        const resolvedCnpj =
          (empresaData.cnpj && String(empresaData.cnpj).replace(/\D/g, "")) ||
          (options?.cnpjLoja?.replace(/\D/g, "") || DELIVAI_INTERNAL_CNPJ);

        const nomeEmpresaHint =
          (hint?.company_name || hint?.nome_empresa || "").trim() || "";
        let merged: Record<string, unknown> = { ...empresaData };
        if (nomeEmpresaHint) {
          merged.nome_empresa = nomeEmpresaHint;
          merged.company_name = nomeEmpresaHint;
        }
        if (hint?.nome) {
          const nomeColab = String(hint.nome).trim();
          if (nomeColab) {
            merged.nome = merged.nome || nomeColab;
            merged.nome_completo = merged.nome_completo || nomeColab;
          }
        }
        if (hint?.cargo != null && merged.cargo == null) merged.cargo = hint.cargo;
        if (hint?.role != null && merged.role == null) merged.role = hint.role;
        if (typeof hint?.agenda_enabled === "boolean") {
          merged.agenda_enabled = hint.agenda_enabled;
        }

        setClientData({
          ...merged,
          cnpj: resolvedCnpj,
          cpf: cleanCpf,
        });

        if (!empresaData.senha_definida) {
          setError(
            'Colaborador encontrado! Use "Primeiro Acesso" (modo colaborador) para criar sua senha.'
          );
          return {
            exists: true,
            firstAccess: true,
            cnpj: resolvedCnpj,
            cpf: cleanCpf,
          };
        }

        console.log("✅ Colaborador encontrado com senha definida");
        return {
          exists: true,
          firstAccess: false,
          cnpj: resolvedCnpj,
          cpf: cleanCpf,
        };
      } else if (data.exists === false) {
        setError("CPF não encontrado. Verifique e tente novamente.");
        setCnpjValidated(false);
        setClientData(null);
        return { exists: false, firstAccess: false };
      } else {
        setError("Erro inesperado na validação. Tente novamente.");
        setCnpjValidated(false);
        setClientData(null);
        return { exists: false, firstAccess: false };
      }
    } catch (error) {
      console.error("Erro na validação do CPF:", error);
      setError("Erro ao validar CPF. Tente novamente.");
      setCnpjValidated(false);
      setClientData(null);
      return { exists: false, firstAccess: false };
    } finally {
      setLoading(false);
    }
  };

  const clearCnpjValidation = () => {
    setCnpjValidated(false);
    setClientData(null);
    setError('');
  };

  const logout = () => {
    setUser(null);
    setCnpjValidated(false);
    setClientData(null);
    setLicenseData(null);
    setLicenseSummary({ diasRestantes: null, loading: false, contaAtiva: null });
    setError('');
    setInitialized(false); // Resetar estado de inicialização
    localStorage.removeItem("delivai_user");
    localStorage.removeItem("delivai_collaborator_cpf");
    localStorage.removeItem("license_modules");
    clearLoginAudience();
    clearLicenseDiasCache();
    localStorage.removeItem("evolution_token");
    localStorage.removeItem("evolution_user");
  };

  const refreshCollaboratorSession = useCallback(async (): Promise<boolean> => {
    const current = userRef.current;
    if (!current?.isCollaborator || !current.cpf) return false;
    try {
      const cpf = current.cpf.replace(/\D/g, "");
      const devSession = isDelivaiDevSession(current);
      const data = await (async () => {
        if (current.cnpj) {
          const withSession = await getCollaboratorProfileWithSession(cpf, current.cnpj);
          if (withSession.exists && withSession.empresa) return withSession;
        }
        return getCollaboratorDataForLogin(cpf, {
          ...(devSession
            ? { internalDelivaiCnpj: true }
            : current.cnpj
              ? { cnpjLoja: current.cnpj }
              : {}),
        });
      })();
      if (!data.exists || !data.empresa) return false;
      const empresaData = (
        Array.isArray(data.empresa) ? data.empresa[0] : data.empresa
      ) as Record<string, unknown>;
      const resolvedCnpj =
        (typeof empresaData.cnpj === "string" && empresaData.cnpj.replace(/\D/g, "")) ||
        (current.cnpj || "").replace(/\D/g, "") ||
        DELIVAI_INTERNAL_CNPJ.replace(/\D/g, "");
      const cargoJson = cargoJsonForCollaboratorLogin(
        empresaData,
        resolvedCnpj,
        devSession
      );
      setUser((prev) => {
        if (!prev?.isCollaborator) return prev;
        const nextUser: User = {
          ...prev,
          name:
            (typeof empresaData.nome === "string" && empresaData.nome) ||
            (typeof empresaData.nome_completo === "string" && empresaData.nome_completo) ||
            prev.name,
          email:
            (typeof empresaData.email === "string" && empresaData.email) ||
            (typeof empresaData.email_cliente === "string" && empresaData.email_cliente) ||
            prev.email,
          cargoJson,
          diaria: parseColaboradorDiaria(empresaData) ?? prev.diaria,
          agendaEnabled: parseAgendaEnabledFlag(empresaData.agenda_enabled),
          phone: collaboratorPhoneFromEmpresa(empresaData) || prev.phone,
          employeeId:
            prev.employeeId ||
            (typeof empresaData.employee_id === "string"
              ? empresaData.employee_id
              : typeof empresaData.id === "string"
                ? empresaData.id
                : undefined),
        };
        localStorage.setItem("delivai_user", JSON.stringify(nextUser));
        return nextUser;
      });
      return true;
    } catch {
      return false;
    }
  }, []);

  const value: AuthContextType = {
    user,
    login,
    logout,
    refreshCollaboratorSession,
    loading,
    validateCnpj,
    validateCollaboratorCpf,
    clearCnpjValidation,
    cnpjValidated,
    clientData,
    error,
    licenseData,
    licenseSummary,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
